import { create } from 'zustand';

import type {
  ToastStore,
  ShowToastOptions,
  ToastNotification,
} from '../types/toast-types';

const MAX_VISIBLE_TOASTS = 2;

export const useToastStore = create<ToastStore>((set, get) => ({
  notifications: [],
  notify: ({
    title,
    message,
    position = 'top-right',
    type = 'default',
    durationMs = 4000,
    id,
    offsetTopPx,
    action,
    onMessageClick,
    messageClassName,
    persistent,
  }: ShowToastOptions) => {
    // Dedup: if a toast with the same message is already visible, return its id without re-showing
    if (message !== undefined) {
      const duplicate = get().notifications.find((n) => n.message === message);
      if (duplicate) return duplicate.id;
    }

    const newId =
      id ?? `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const item: ToastNotification = {
      id: newId,
      title,
      message,
      position,
      type,
      durationMs,
      createdAt: Date.now(),
      offsetTopPx,
      action,
      onMessageClick,
      messageClassName,
      persistent,
    };

    set((state) => {
      const nonPersistent = state.notifications.filter((n) => !n.persistent);
      let notifications = state.notifications;

      // Cap non-persistent toasts at MAX_VISIBLE_TOASTS by evicting the oldest (first inserted)
      if (!persistent && nonPersistent.length >= MAX_VISIBLE_TOASTS) {
        notifications = notifications.filter(
          (n) => n.id !== nonPersistent[0].id,
        );
      }

      return { notifications: [...notifications, item] };
    });

    return newId;
  },
  remove: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },
  // Removes only non-persistent toasts. Persistent toasts remain visible and must be removed individually via remove()
  clear: () =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.persistent),
    })),
}));
